import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import dateFormat from 'dateformat';
import { startOfMinute } from 'date-fns';
import Menu from '@mui/material/Menu';
import DeleteAddressbookDialog from '#component/addressbook/dashboard/delete-addressbook-dialog';
import ReportDialog from '#component/warehouse/appliance-overview/report-dialog';
import StopDialog from '#component/warehouse/appliance-overview/stop-dialog';
import { deleteAppliance, stopAppliance } from '#action/warehouse/appliance/appliance';
import { fetchAppliances } from '#action/warehouse/appliance/appliances';
import { isEnabledModuleById } from '#component/project/form/helper';
import { getAvailableDateRange } from '#component/warehouse/appliance-overview/helper';
import {
  StyledMenuItem,
  DisabledText,
  StyledPreloader,
  SettingsIcon,
  NotesIcon,
  SaveIcon,
  DisabledSaveIcon,
  StopIcon,
  DeleteIcon
} from './styles';

/**
 * Renders a warehouse appliances actions and delete dialog.
 * @returns {React.FC}
 */

const APPLIANCE_STATUS = {
  STOPPED: 300
};

const ApplianceListActions = props => {
  const {
    anchorEl,
    setAnchorEl = () => null,
    appliance,
    selectedDevices,
    userType,
    history
  } = props;
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showReportDialog, setReportDialog] = useState(false);

  const dispatch = useDispatch();

  const trailModuleEnabled = isEnabledModuleById(appliance.project.modules, 3);

  const [showStopDialog, setShowStopDialog] = useState(false);
  const [isLocallyStop, setIsLocallyStop] =
    useState(appliance?.status === APPLIANCE_STATUS.STOPPED);

  const isMetricsLoaded = selectedDevices
    .some(d => Object.values(d.metrics ?? {}).some(v => v.length));
  const emptyMetrics = selectedDevices.every(d => d.metrics) && !isMetricsLoaded;

  const isApplianceStop = isLocallyStop || appliance?.status === APPLIANCE_STATUS.STOPPED;

  useEffect(() => {
    setIsLocallyStop(appliance?.status === APPLIANCE_STATUS.STOPPED);
  }, [appliance]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setOpen(true);
  };

  const handleDeleteClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    const result = await dispatch(deleteAppliance(appliance?.id));
    if (result?.success) {
      handleDeleteClose();
      history.push('/warehouse');
    }
  };

  const handleStopClose = () => {
    setShowStopDialog(false);
    setAnchorEl(null);
  };

  const handleStop = async ({ stopDate }) => {
    const result = await dispatch(stopAppliance(appliance?.id, { stopDate }));
    if (result?.success) {
      setIsLocallyStop(true);
      if (location.pathname.includes('/appliance-overview') && appliance?.id) {
        const s = dateFormat(startOfMinute(stopDate), 'isoUtcDateTime');
        const { start, end } = getAvailableDateRange(
          appliance.creationDate,
          s,
          3
        );
        history.replace(`/warehouse/appliance-overview/${appliance?.id}/t/0/dr/${start}/${end}`);
      }
      handleStopClose();
      dispatch(fetchAppliances());
    }
  };

  return (
    <>
      {userType <= 20 && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {userType < 10 && (
            <StyledMenuItem
              component={Link}
              to={`/warehouse/appliance/edit/${appliance?.id}`}
            >
              <SettingsIcon />
              Settings
            </StyledMenuItem>
          )}
          {trailModuleEnabled && (
            <StyledMenuItem
              component={Link}
              to={`/warehouse-log?appliance=${appliance?.id}`}
            >
              <NotesIcon />
              Audit trail
            </StyledMenuItem>
          )}
          {isMetricsLoaded ? (
            <StyledMenuItem
              onClick={() => {
                setAnchorEl(null);
                setReportDialog(true);
              }}
            >
              <SaveIcon />
              <span>Report</span>
            </StyledMenuItem>
          ) : emptyMetrics ? (
            <StyledMenuItem>
              <DisabledSaveIcon />
              <DisabledText>Report</DisabledText>
            </StyledMenuItem>
          ) : (
            <StyledMenuItem>
              <StyledPreloader size={20} />
              <DisabledText>Report</DisabledText>
            </StyledMenuItem>
          )}
          {userType < 10 && !isApplianceStop && (
            <StyledMenuItem
              onClick={() => {
                setAnchorEl(null);
                setShowStopDialog(true);
              }}
            >
              <StopIcon />
              <span>Stop</span>
            </StyledMenuItem>
          )}
          {userType < 10 && (
            <StyledMenuItem onClick={handleDeleteClick}>
              <DeleteIcon />
              Delete
            </StyledMenuItem>
          )}
        </Menu>
      )}
      <DeleteAddressbookDialog
        open={open}
        dialogTitle={`Do you want to delete the "${appliance?.label ?? ''}"?`}
        entityTitle={appliance?.label ?? ''}
        dialogContentText="Type the application label to confirm"
        textFieldLabel="Label"
        onClose={handleDeleteClose}
        onDelete={handleDelete}
      />
      <ReportDialog
        showDialog={showReportDialog}
        setReportDialog={setReportDialog}
        {...props}
      />
      <StopDialog
        appliance={appliance}
        dispatch={dispatch}
        showDialog={showStopDialog}
        setShowDialog={setShowStopDialog}
        onClose={handleStopClose}
        onStop={handleStop}
      />
    </>
  );
};

export default ApplianceListActions;
